import { useEffect, useState, useCallback } from "react";
import { Download, Search, ShieldCheck } from "lucide-react";
import AdminShell from "../../components/AdminShell";
import { ActionButton, CustomerInput, CustomerPanel, CustomerSelect, TableWrapper } from "../../components/customer/CustomerUI";
import api from "../../lib/api";

const CATEGORIES = ["", "authentication", "security", "authorization", "admin", "crm_connection", "crm_scan", "credit", "config", "api"];
const CAT_COLOR = {
  authentication: "text-sky-400", security: "text-red-400", authorization: "text-red-400",
  admin: "text-intel", crm_connection: "text-emerald-400", crm_scan: "text-amber-400", credit: "text-intel",
};
const PAGE = 25;

export default function AuditCenter() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [actorRole, setActorRole] = useState("");
  const [targetType, setTargetType] = useState("");
  const [metadataQ, setMetadataQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [skip, setSkip] = useState(0);
  const [chain, setChain] = useState(null);

  const load = useCallback(async () => {
    const params = { limit: PAGE, skip };
    if (category) params.category = category;
    if (status) params.status = status;
    if (q) params.q = q;
    if (actorRole) params.actor_role = actorRole;
    if (targetType) params.target_type = targetType;
    if (metadataQ) params.metadata_q = metadataQ;
    if (fromDate) params.from_ts = new Date(`${fromDate}T00:00:00`).toISOString();
    if (toDate) params.to_ts = new Date(`${toDate}T23:59:59.999`).toISOString();
    const { data } = await api.get("/admin/audit", { params });
    setItems(data.items);
    setTotal(data.total);
  }, [category, status, q, actorRole, targetType, metadataQ, fromDate, toDate, skip]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/audit/verify").then((r) => setChain(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    if (q) params.q = q;
    if (actorRole) params.actor_role = actorRole;
    if (targetType) params.target_type = targetType;
    if (metadataQ) params.metadata_q = metadataQ;
    if (fromDate) params.from_ts = new Date(`${fromDate}T00:00:00`).toISOString();
    if (toDate) params.to_ts = new Date(`${toDate}T23:59:59.999`).toISOString();
    const res = await api.get("/admin/audit/export", { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "cleaniq_audit.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminShell title="Audit Center" subtitle="Immutable, hash-chained record of every platform action">
      <CustomerPanel className="mb-4">
      <div className="flex flex-wrap items-center gap-2 px-ui-4 py-ui-4 sm:px-ui-5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <CustomerInput data-testid="audit-search" value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }}
                 placeholder="Search action, actor, target, IP…" className="w-72 pl-8" />
        </div>
        <CustomerSelect data-testid="audit-category-filter" value={category} onChange={(e) => { setSkip(0); setCategory(e.target.value); }}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c || "All categories"}</option>)}
        </CustomerSelect>
        <CustomerSelect data-testid="audit-status-filter" value={status} onChange={(e) => { setSkip(0); setStatus(e.target.value); }}>
          <option value="">All statuses</option>
          <option value="success">success</option>
          <option value="failure">failure</option>
        </CustomerSelect>
        <CustomerSelect data-testid="audit-role-filter" value={actorRole} onChange={(e) => { setSkip(0); setActorRole(e.target.value); }}>
          <option value="">All actor roles</option><option value="admin">admin</option><option value="user">user</option>
        </CustomerSelect>
        <CustomerInput data-testid="audit-target-filter" value={targetType} onChange={(e) => { setSkip(0); setTargetType(e.target.value); }}
               placeholder="Target type" className="w-36" />
        <CustomerInput data-testid="audit-metadata-filter" value={metadataQ} onChange={(e) => { setSkip(0); setMetadataQ(e.target.value); }}
               placeholder="Metadata contains" className="w-44" />
        <label className="flex items-center gap-1 text-xs text-slate-500">From <CustomerInput type="date" value={fromDate} onChange={(e) => { setSkip(0); setFromDate(e.target.value); }} /></label>
        <label className="flex items-center gap-1 text-xs text-slate-500">To <CustomerInput type="date" value={toDate} onChange={(e) => { setSkip(0); setToDate(e.target.value); }} /></label>
        <ActionButton variant="secondary" data-testid="audit-export-button" onClick={exportCsv}>
          <Download className="h-4 w-4" /> Export CSV
        </ActionButton>
        {chain && (
          <span className={`ml-auto flex items-center gap-1.5 font-mono text-[11px] ${chain.intact ? "text-emerald-400" : "text-red-400"}`} data-testid="audit-chain-status">
            <ShieldCheck className="h-3.5 w-3.5" /> {chain.intact ? "Chain verified" : "Tamper detected"}
          </span>
        )}
      </div>
      </CustomerPanel>

      <TableWrapper title="Audit events">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-300 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <th className="px-3 py-2 font-medium">Seq</th>
              <th className="px-3 py-2 font-medium">Timestamp</th>
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Actor</th>
              <th className="px-3 py-2 font-medium">Target</th>
              <th className="px-3 py-2 font-medium">Metadata</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No audit events.</td></tr>
            ) : items.map((e) => (
              <tr key={e.id} data-testid={`audit-row-${e.seq}`} className="border-b border-slate-300 last:border-0">
                <td className="px-3 py-2 font-mono text-slate-500">{e.seq}</td>
                <td className="px-3 py-2 font-mono text-slate-600">{(e.ts || "").replace("T", " ").slice(0, 19)}</td>
                <td className={`px-3 py-2 font-mono ${CAT_COLOR[e.category] || "text-slate-600"}`}>{e.category}</td>
                <td className="px-3 py-2 text-slate-900">{e.action}</td>
                <td className="px-3 py-2 text-slate-600">{e.actor_email || "—"}</td>
                <td className="px-3 py-2 font-mono text-slate-500">{e.target_id || "—"}</td>
                <td className="max-w-64 truncate px-3 py-2 font-mono text-slate-500" title={JSON.stringify(e.metadata || {})}>{Object.keys(e.metadata || {}).length ? JSON.stringify(e.metadata) : "—"}</td>
                <td className="px-3 py-2"><span className={`font-mono ${e.status === "failure" ? "text-red-400" : "text-emerald-400"}`}>{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span>{total} events · showing {skip + 1}–{Math.min(skip + PAGE, total)}</span>
        <div className="flex gap-2">
          <ActionButton variant="secondary" data-testid="audit-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}>Prev</ActionButton>
          <ActionButton variant="secondary" data-testid="audit-next" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}>Next</ActionButton>
        </div>
      </div>
    </AdminShell>
  );
}
